// AddEspacioModal.tsx — Co Marca — Crear Espacio
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Store, Users, MapPin, Info } from 'lucide-react';
import { createEspacio } from '../../services/espacioService';
import { fetchSucursales, Sucursal } from '../../services/pos/sucursalService';
import ImageUpload from '../Upload/ImageUpload';

interface AddEspacioModalProps {
    isOpen: boolean; onClose: () => void; onEspacioAdded: () => void;
}

const colors = {
    primary:        '#c1348b',
    primaryDark:    '#8f1f64',
    secondary:      '#61257b',
    secondaryLight: '#8b3fa8',
    accentDark:     '#ea8406',
};

const css = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&display=swap');
.aem-modal *{font-family:'DM Sans',sans-serif;box-sizing:border-box;}
@keyframes aemFadeIn{from{opacity:0;}to{opacity:1;}}
@keyframes aemSlideUp{from{opacity:0;transform:translateY(24px) scale(.98);}to{opacity:1;transform:translateY(0) scale(1);}}
@keyframes spin{to{transform:rotate(360deg);}}
.aem-overlay{animation:aemFadeIn .22s ease both;}
.aem-dialog{animation:aemSlideUp .32s cubic-bezier(.16,1,.3,1) both;}
.aem-input{width:100%;padding:10px 14px;border-radius:10px;border:1.5px solid #e5e7eb;background:#fafafa;font-size:.88rem;color:#374151;outline:none;transition:all .2s;}
.aem-input:focus{border-color:${colors.primary};box-shadow:0 0 0 3px ${colors.primary}12;background:#fff;}
.aem-textarea{resize:none;min-height:100px;}
.aem-select{appearance:none;}
.aem-btn-primary{display:inline-flex;align-items:center;gap:7px;background:linear-gradient(135deg,${colors.primary},${colors.primaryDark});color:#fff;border:none;border-radius:11px;padding:10px 22px;font-size:.88rem;font-weight:800;cursor:pointer;box-shadow:0 4px 14px rgba(193, 52, 139,.28);transition:all .22s cubic-bezier(.16,1,.3,1);}
.aem-btn-primary:hover:not(:disabled){transform:translateY(-1px);box-shadow:0 8px 22px rgba(193, 52, 139,.34);}
.aem-btn-primary:disabled{background:#e2e8f0;color:#94a3b8;box-shadow:none;cursor:not-allowed;}
.aem-btn-ghost{display:inline-flex;align-items:center;gap:7px;background:#f8fafc;border:1.5px solid #e2e8f0;color:#374151;border-radius:11px;padding:10px 18px;font-size:.88rem;font-weight:700;cursor:pointer;transition:all .2s;}
.aem-btn-ghost:hover{background:#f1f5f9;}
.custom-scroll::-webkit-scrollbar{width:6px;}
.custom-scroll::-webkit-scrollbar-track{background:transparent;}
.custom-scroll::-webkit-scrollbar-thumb{background:rgba(97, 37, 123,.3);border-radius:20px;}
.custom-scroll::-webkit-scrollbar-thumb:hover{background:rgba(97, 37, 123,.5);}
`;

const AddEspacioModal: React.FC<AddEspacioModalProps> = ({ isOpen, onClose, onEspacioAdded }) => {
    const [formData, setFormData] = useState({
        nombre:'', descripcion:'', cantidadPersonas:'',
        imagen: null as File|null, estado:true, sucursal:''
    });
    const [sucursales, setSucursales] = useState<Sucursal[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError]         = useState<string|null>(null);

    useEffect(() => {
        if (isOpen) {
            setFormData({ nombre:'', descripcion:'', cantidadPersonas:'', imagen:null, estado:true, sucursal:'' });
            setError(null);
            fetchSucursales().then(d=>setSucursales(d.filter(s=>s.activo))).catch(()=>setError('Error al cargar sucursales.'));
        }
    }, [isOpen]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.sucursal) { setError('Debe seleccionar una sucursal'); return; }
        if (!formData.cantidadPersonas || parseInt(formData.cantidadPersonas)<1) { setError('La capacidad debe ser al menos 1'); return; }
        setIsLoading(true); setError(null);
        try {
            await createEspacio({ nombre:formData.nombre, descripcion:formData.descripcion, cantidad_personas:parseInt(formData.cantidadPersonas), imagen:formData.imagen, estado:formData.estado, sucursal:parseInt(formData.sucursal) });
            onEspacioAdded(); onClose();
        } catch { setError('Error al crear el espacio. Intente nuevamente.'); }
        finally { setIsLoading(false); }
    };

    if (!isOpen) return null;

    return createPortal(
        <>
            <style>{css}</style>
            <div className="aem-modal" style={{ position:'fixed', inset:0, zIndex:99999, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}>
                <div className="aem-overlay" onClick={onClose} style={{ position:'absolute', inset:0, background:'rgba(15,23,42,.55)', backdropFilter:'blur(6px)' }}/>
                <div className="aem-dialog custom-scroll" style={{ position:'relative', background:'#fff', borderRadius:'24px', width:'100%', maxWidth:'640px', maxHeight:'92vh', overflowY:'auto', boxShadow:'0 32px 80px rgba(0,0,0,.18)', border:'1.5px solid #f1f5f9' }}>

                    <div style={{ height:'4px', background:`linear-gradient(90deg,${colors.primary},${colors.secondary},${colors.accentDark},${colors.secondaryLight})`, borderRadius:'24px 24px 0 0' }}/>

                    {/* Header */}
                    <div style={{ padding:'24px 28px 0', display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
                        <div>
                            <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'8px' }}>
                                <div style={{ width:'36px', height:'36px', borderRadius:'11px', background:`linear-gradient(135deg,${colors.primary},${colors.primaryDark})`, display:'flex', alignItems:'center', justifyContent:'center', boxShadow:`0 4px 12px ${colors.primary}30` }}>
                                    <Plus size={18} color="#fff"/>
                                </div>
                                <div>
                                    <h3 style={{ fontSize:'1.1rem', fontWeight:'900', color:'#0f172a', letterSpacing:'-.02em' }}>Nuevo Espacio</h3>
                                    <p style={{ fontSize:'.72rem', color:'#94a3b8', fontWeight:'500' }}>Completa los datos del espacio físico</p>
                                </div>
                            </div>
                            <div style={{ display:'flex', gap:'4px' }}>
                                {[colors.primary,colors.secondary,colors.accentDark,colors.secondaryLight].map((c,i)=>(
                                    <div key={i} style={{ height:'2px', width:i===0?'18px':'6px', borderRadius:'2px', background:c }}/>
                                ))}
                            </div>
                        </div>
                        <button onClick={onClose} style={{ width:'32px', height:'32px', borderRadius:'10px', border:'1.5px solid #f1f5f9', background:'#f8fafc', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0 }}
                            onMouseEnter={e=>(e.currentTarget as HTMLButtonElement).style.background='#f1f5f9'}
                            onMouseLeave={e=>(e.currentTarget as HTMLButtonElement).style.background='#f8fafc'}>
                            <X size={15} color="#64748b"/>
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} style={{ padding:'20px 28px 28px', display:'flex', flexDirection:'column', gap:'16px' }}>

                        {error && (
                            <div style={{ display:'flex', alignItems:'center', gap:'8px', background:`${colors.primary}08`, border:`1.5px solid ${colors.primary}22`, borderRadius:'10px', padding:'10px 14px' }}>
                                <X size={14} color={colors.primary}/>
                                <span style={{ fontSize:'.82rem', color:colors.primaryDark, fontWeight:'600' }}>{error}</span>
                            </div>
                        )}

                        {/* Sucursal + Nombre */}
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px' }}>
                            <div>
                                <label style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'.72rem', fontWeight:'800', color:'#374151', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px' }}>
                                    <MapPin size={11} color={colors.secondary}/> Sucursal *
                                </label>
                                <select required value={formData.sucursal} onChange={e=>setFormData({...formData,sucursal:e.target.value})} className="aem-input aem-select">
                                    <option value="">Seleccione una sucursal</option>
                                    {sucursales.map(s=><option key={s.id} value={s.id}>{s.nombre} ({s.codigo})</option>)}
                                </select>
                            </div>
                            <div>
                                <label style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'.72rem', fontWeight:'800', color:'#374151', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px' }}>
                                    <Store size={11} color={colors.secondary}/> Nombre *
                                </label>
                                <input type="text" required value={formData.nombre} onChange={e=>setFormData({...formData,nombre:e.target.value})} placeholder="Ej: Sala de Conferencias" className="aem-input"/>
                            </div>
                        </div>

                        {/* Descripción + Imagen */}
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px' }}>
                            <div>
                                <label style={{ display:'block', fontSize:'.72rem', fontWeight:'800', color:'#374151', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px' }}>Descripción</label>
                                <textarea value={formData.descripcion} onChange={e=>setFormData({...formData,descripcion:e.target.value})} placeholder="Características, equipamiento, etc." className="aem-input aem-textarea"/>
                            </div>
                            <div>
                                <label style={{ display:'block', fontSize:'.72rem', fontWeight:'800', color:'#374151', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px' }}>Imagen</label>
                                <div style={{ height:'100px' }}>
                                    <ImageUpload setImagen={(file)=>setFormData({...formData,imagen:file})}/>
                                </div>
                            </div>
                        </div>

                        {/* Capacidad + Estado */}
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px' }}>
                            <div>
                                <label style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'.72rem', fontWeight:'800', color:'#374151', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px' }}>
                                    <Users size={11} color={colors.accentDark}/> Capacidad *
                                </label>
                                <input type="number" required min="1" value={formData.cantidadPersonas} onChange={e=>setFormData({...formData,cantidadPersonas:e.target.value})} placeholder="Ej: 10" className="aem-input"/>
                                <p style={{ fontSize:'.68rem', color:'#94a3b8', marginTop:'5px' }}>Máximo de personas simultáneas</p>
                            </div>
                            <div style={{ background:'#f8fafc', border:'1.5px solid #f1f5f9', borderRadius:'14px', padding:'14px 16px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                                <div>
                                    <p style={{ fontSize:'.85rem', fontWeight:'700', color:'#0f172a', marginBottom:'2px' }}>Estado del espacio</p>
                                    <p style={{ fontSize:'.7rem', color:formData.estado?colors.secondaryLight:'#94a3b8', fontWeight:'600' }}>
                                        {formData.estado?'● Disponible para reservas':'○ No disponible'}
                                    </p>
                                </div>
                                <div onClick={()=>setFormData({...formData,estado:!formData.estado})} style={{ width:'44px', height:'24px', borderRadius:'100px', background:formData.estado?colors.secondary:'#d1d5db', position:'relative', cursor:'pointer', transition:'background .25s', flexShrink:0 }}>
                                    <div style={{ position:'absolute', top:'3px', left:formData.estado?'23px':'3px', width:'18px', height:'18px', borderRadius:'50%', background:'#fff', boxShadow:'0 1px 4px rgba(0,0,0,.2)', transition:'left .25s' }}/>
                                </div>
                            </div>
                        </div>

                        {/* Nota */}
                        <div style={{ display:'flex', alignItems:'flex-start', gap:'8px', background:`${colors.secondary}08`, border:`1.5px solid ${colors.secondary}18`, borderRadius:'10px', padding:'10px 14px' }}>
                            <Info size={14} color={colors.secondary} style={{ flexShrink:0, marginTop:'1px' }}/>
                            <p style={{ fontSize:'.75rem', color:'#374151', lineHeight:1.55 }}>
                                Los campos marcados con <strong>*</strong> son obligatorios. La imagen puede ser subida en JPG, PNG o WEBP.
                            </p>
                        </div>

                        <div style={{ display:'flex', justifyContent:'flex-end', gap:'10px', paddingTop:'4px' }}>
                            <button type="button" onClick={onClose} className="aem-btn-ghost">Cancelar</button>
                            <button type="submit" disabled={isLoading} className="aem-btn-primary">
                                {isLoading ? <><span style={{ width:'13px',height:'13px',border:'2px solid rgba(255,255,255,.4)',borderTopColor:'#fff',borderRadius:'50%',animation:'spin .7s linear infinite',display:'inline-block' }}/> Creando...</> : <><Plus size={14}/> Crear Espacio</>}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </>,
        document.body
    );
};

export default AddEspacioModal;